/**
 * The Control Center's external-service sections: Telegram and WhatsApp
 * account linking,
 * Google Document AI, Google sign-in, the AI backends and prompt parameters,
 * and the stored-secret fragment their credential forms share. The clinic's
 * Telegram bots are the Connectors section's (`controlCenterConnectors`),
 * not a leaf here.
 *
 * Split from `controlCenter` because these sections talk about services
 * outside Terysling — tokens, providers, backends — a vocabulary the rest of
 * the Control Center never uses.
 *
 * Composed into the public catalogue by `catalogue.en.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const controlCenterIntegrations = {
  'ai.functions.function': "Function",
  'ai.functions.pipeline': "Pipeline",
  'ai.functions.pipelineFor': "Pipeline for {name}",
  'ai.functions.prompts': "Prompts",
  'ai.functions.parametersFor': "Parameters: {name}",
  'ai.functions.enabled': "Enabled",
  'ai.functions.enabledFor': "Enable: {name}",
  'ai.functions.unsaved': "Not saved",
  'ai.functions.pendingFor': "Unsaved selection: {name}.",
  'ai.functions.apply': "Apply pipeline selection",
  'ai.functions.cancel': "Cancel selection changes",
  'ai.functions.applied': "Saved. New requests use this selection.",
  'ai.functions.noPrompts': "No registry prompts",
  'ai.functions.promptUnavailable': "Details unavailable",
  'ai.functions.fixedSettings': "These execution parameters are read-only.",
  'ai.functions.empty': "No AI functions available",
  'ai.functions.governmentId': "Extract identity document details",
  'ai.functions.insurance': "Extract insurance policy details",
  'ai.functions.transcription': "Transcribe speech to text",
  'ai.functions.transcriptionStage': "Speech transcription",
  'ai.functions.dataOCR': "Google receives document page images. OpenAI receives the recognized text and normalized page images for verification.",
  'ai.functions.dataDictation': "OpenAI receives the audio, then the transcript and clinical form schema for field extraction.",
  'ai.functions.dataAudio': "OpenAI receives the recorded audio.",
  'ai.functions.dataText': "OpenAI receives the selected text and applicable language support files.",
  'ai.functions.dataDocument': "OpenAI receives document content, including page images required by this function.",
  'ai.functions.dataLabGooglePages': "Google Document AI receives each page alone. OpenAI receives the recognized text of every page, then the images and text of the pages you confirm.",
  'ai.functions.dataQuestion': "OpenAI receives the clinical PDF, the question and this doctor’s previous questions for the event.",
  'ai.functions.dataClinical': "OpenAI receives clinical event evidence to suggest recommendations.",
  'ai.functions.dataFilters': "OpenAI receives column headings and the search description; table rows are not sent.",
  'ai.functions.dataNames': "OpenAI receives the source text and requested languages.",
  'ai.functions.dataIssue': "OpenAI receives the issue description; attachments are not sent.",
  'ai.functions.dataExternalEntries': "OpenAI receives the recognized text of the chosen documents' pages to mark them, group them into episodes of care and draft one entry per episode.",
  'ai.functions.dataSummary': "OpenAI receives the filled fields of the chart's records and the recognized text of the patient's documents.",
  'ai.functions.dataUnknown': "Data required by this function is sent to the listed providers.",
  'ai.functions.title': "AI functions",
  'ai.functions.description': 'Disabling blocks new requests; admitted work may finish.',
  'ai.functions.loading': "Loading function settings…",
  'ai.functions.unavailable': "Function settings are unavailable. Try again after they reload.",
  'ai.functions.disabled': "Disabled",
  'ai.functions.disabledFor': "Disable: {name}",
  'ai.functions.disabledHint': "Disabled in Control Center AI settings.",
  'ai.functions.external': "Data is sent to external providers:",
  'ai.functions.local': "Processing stays within the local environment.",
  'ai.functions.googleOpenai': "Google OCR + OpenAI verification",
  'ai.functions.googleIDOpenai': "Google OCR + OpenAI extraction",
  'ai.functions.googlePagesOpenai': "Google page by page + OpenAI extraction",
  'ai.functions.dataIDGoogleLocal': "Google receives document pages for OCR. The local model receives the OCR text and normalized images; they are not sent to OpenAI.",
  'ai.functions.dataIDGoogleOpenai': "Google receives document pages for OCR. OpenAI receives the OCR text and normalized images to extract identity details.",
  'ai.functions.dataInsuranceGoogleOpenai': "Google receives document pages for OCR. OpenAI receives the OCR text and normalized images to extract insurance policy details.",
  'ai.functions.googleLocal': "Google OCR + local model",
  'ai.functions.localOpenai': "Local page alignment + OpenAI OCR",
  'ai.functions.ocr': "Recognize document text (OCR)",
  'ai.functions.dictation': "Fill a clinical record from dictation",
  'ai.functions.translateDocument': "Document translation",
  'ai.functions.translateRecord': "Clinical record translation",
  'ai.functions.summary': "Document summary",
  'ai.functions.questions': "Questions about clinical documents",
  'ai.functions.review': "Suggest clinical recommendations",
  'ai.functions.filters': "Table filter suggestions",
  'ai.functions.localizedText': "Name translation suggestions",
  'ai.functions.issueTitle': "Issue title suggestions",
  'ai.functions.unknown': "Unsupported function or pipeline — update the application",
  'ai.functions.unsavedIntent': "AI function settings",
  'ai.functions.reload': "Load saved settings and replace draft",
  'ai.functions.reloaded': "Saved settings loaded.",
  'ai.functions.reloadFailed': "Could not reload settings. Your draft is preserved.",
  'telegramLink.title': 'Telegram',
  'telegramLink.loading': 'Loading Telegram connection',
  'telegramLink.loadFailed': 'The Telegram connection could not be loaded.',
  'telegramLink.connected': 'Connected',
  'telegramLink.notConnected': 'Not connected',
  'telegramLink.createInvite': 'Create invite link',
  'telegramLink.creating': 'Creating link',
  'telegramLink.inviteLink': 'One-time Telegram invite link',
  'telegramLink.copyInvite': 'Copy invite link',
  'telegramLink.openTelegram': 'Open Telegram',
  'telegramLink.expiresInDay': 'This one-time link expires in 24 hours.',
  'telegramLink.copied': 'Invite link copied',
  'telegramLink.copyFailed': 'The invite link could not be copied.',
  'telegramLink.botUnavailable': 'The clinic Telegram bot is not configured yet.',
  'telegramLink.inviteFailed': 'The invite link could not be created.',
  'telegramLink.disconnect': 'Disconnect',
  'telegramLink.disconnecting': 'Disconnecting',
  'telegramLink.disconnectFailed': 'Telegram could not be disconnected.',
  'whatsappLink.title': 'WhatsApp',
  'whatsappLink.loading': 'Loading WhatsApp connection',
  'whatsappLink.loadFailed': 'The WhatsApp connection could not be loaded.',
  'whatsappLink.connected': 'Connected',
  'whatsappLink.notConnected': 'Not connected',
  'whatsappLink.createInvite': 'Create invite link',
  'whatsappLink.creating': 'Creating link',
  'whatsappLink.inviteLink': 'One-time WhatsApp invite link',
  'whatsappLink.copyInvite': 'Copy invite link',
  'whatsappLink.openWhatsApp': 'Open WhatsApp',
  'whatsappLink.sendToLink':
    'The link opens a WhatsApp chat with the clinic number. Sending the prefilled message links the phone it is sent from.',
  'whatsappLink.expiresInDay': 'This one-time link expires in 24 hours.',
  'whatsappLink.copied': 'Invite link copied',
  'whatsappLink.copyFailed': 'The invite link could not be copied.',
  'whatsappLink.senderUnavailable': 'The clinic WhatsApp sender is not configured yet.',
  'whatsappLink.inviteFailed': 'The invite link could not be created.',
  'whatsappLink.disconnect': 'Disconnect',
  'whatsappLink.disconnecting': 'Disconnecting',
  'whatsappLink.disconnectFailed': 'WhatsApp could not be disconnected.',

  'storedSecret.updated': 'Updated',
  'storedSecret.length': '{length} characters',


  'googleDocAI.title': 'Google Document AI',
  'googleDocAI.loading': 'Loading Google Document AI status',
  'googleDocAI.unavailable': 'Encrypted clinic secret storage is not configured.',
  'googleDocAI.loadFailed': 'The Google Document AI status could not be loaded.',
  'googleDocAI.serviceAccount': 'Service account',
  'googleDocAI.project': 'Project',
  'googleDocAI.key': 'Service-account key file content',
  // The FACT, not the form field: what is previewed is the private key inside
  // the file, so the row is named for that and not for the file around it.
  'googleDocAI.storedKey': 'Private key',
  'googleDocAI.set': 'Set service-account key',
  'googleDocAI.change': 'Replace service-account key',
  'googleDocAI.openFile': 'Open key file',
  'googleDocAI.keyFilePicker': 'Choose the service-account key file',
  'googleDocAI.fromFile': 'Read from {name}',
  'googleDocAI.fileReadFailed': 'The chosen file could not be read.',
  'googleDocAI.location': 'Processor location',
  'googleDocAI.processorId': 'Processor ID',
  'googleDocAI.unsavedIntent': 'Google Document AI key',
  'googleDocAI.save': 'Save key',
  'googleDocAI.saving': 'Checking and saving',
  'googleDocAI.clear': 'Remove key',
  'googleDocAI.clearing': 'Removing',
  'googleDocAI.saved': 'Google Document AI key saved',
  'googleDocAI.cleared': 'Google Document AI key removed',
  'googleDocAI.rejected': 'This is not the content of a Google service-account key file.',
  'googleDocAI.settingsRejected': 'The processor location or ID is not valid.',
  'googleDocAI.saveFailed': 'The Google Document AI key could not be saved.',
  'googleDocAI.clearFailed': 'The Google Document AI key could not be removed.',

  "googleSignIn.title": "Patient sign-in with Google",
  'googleSignIn.loading': 'Loading Google sign-in status',
  'googleSignIn.loadFailed': 'The Google sign-in setting could not be loaded.',
  'googleSignIn.clientId': 'OAuth web client ID',
  'googleSignIn.hint':
    'The public web client ID from Google Cloud Console, ending in .apps.googleusercontent.com. Patients see the Google button on the public onboarding page only while it is set.',
  'googleSignIn.set': 'Set client ID',
  'googleSignIn.change': 'Replace client ID',
  'googleSignIn.unsavedIntent': 'Google sign-in client ID',
  'googleSignIn.save': 'Save client ID',
  'googleSignIn.saving': 'Checking and saving',
  'googleSignIn.clear': 'Remove client ID',
  'googleSignIn.clearing': 'Removing',
  'googleSignIn.saved': 'Google sign-in client ID saved',
  'googleSignIn.cleared': 'Google sign-in client ID removed',
  'googleSignIn.rejected': 'This is not a Google web client ID ending in .apps.googleusercontent.com.',
  'googleSignIn.saveFailed': 'The Google sign-in client ID could not be saved.',
  'googleSignIn.clearFailed': 'The Google sign-in client ID could not be removed.',

  'ai.title': 'AI',
  'ai.views.label': 'AI subsections',
  'ai.views.settings': "Functions",
  'ai.views.settingsDescription': "Pipelines, prompts and execution settings",
  'ai.views.prompts': 'Prompts',
  'ai.views.promptsDescription': 'The registry and its per-clinic parameters',
  'ai.views.files': 'Files',
  'ai.views.filesDescription': 'Modules and glossaries every run attaches',
  'ai.views.credentials': 'Credentials',
  'ai.views.credentialsDescription': 'Service keys and access credentials',
  'ai.provider.title': 'Document processing backends',
  'ai.provider.description':
    'Each operation runs where this clinic sends it. The three choices are saved together.',
  'ai.provider.openai': 'OpenAI',
  'ai.provider.local': 'Local',
  'ai.provider.loading': 'Loading document processing backends',
  'ai.provider.loadFailed': 'Could not load AI function settings',
  'ai.provider.save': 'Save AI settings',
  'ai.provider.saving': 'Saving AI settings',
  'ai.provider.saved': 'AI function settings saved',
  'ai.provider.saveFailed': 'Could not save AI function settings',
  'ai.provider.saveForbidden': 'You may not change AI function settings for this clinic',
  'ai.provider.saveConflict':
    'Someone else changed these AI settings. Reload the page and choose again.',
  'ai.provider.unsavedIntent': 'AI backend choices \u2014 not yet saved',
  'ai.backend.deidentification': 'Anonymization',
  'ai.backend.deidentificationNote':
    'Removes the patient identity from a document before it leaves the clinic.',
  'ai.backend.classification': 'Classification',
  'ai.backend.classificationNote':
    'Decides what an uploaded document is, so it files itself in the right place.',
  'ai.backend.laboratory': 'Laboratory result extraction',
  'ai.backend.laboratoryNote':
    'Reads analyte values out of a laboratory report into the patient record.',
  'ai.tester.title': 'Document tester',
  'ai.tester.open': 'Open document tester',
  'ai.tester.hint': 'Developer-only Gradio workspace for classification, laboratory extraction and anonymization.',
  'ai.prompts.title': 'Prompt registry',
  'ai.prompts.note':
    'Prompts are registered by the AI service. The parameters recorded here apply to this clinic only.',
  'ai.loading': 'Loading AI prompts',
  'ai.loadFailed': 'Could not load AI prompts',
  'ai.forbidden': 'You may not read AI prompts for this clinic',
  'ai.empty': 'No AI prompts are registered',
  'ai.prompt': 'Prompt',
  'ai.mode': 'Mode',
  'ai.modeResponses': 'Responses API',
  'ai.modeLocalVlm': "Local vision model",
  'ai.modeAgent': 'Agent',
  'ai.model': 'Model',
  'ai.turns': 'Turns',
  'ai.output': 'Output tokens',
  'ai.tools': 'Tools',
  'ai.providerState': 'Provider state',
  'ai.providerStateOn': 'On',
  'ai.providerStateOff': 'Off',
  'ai.langfuse': 'Langfuse',
  'ai.reasoning': 'Reasoning',
  'ai.reasoningUnset': 'Not set',
  'ai.verbosity': 'Verbosity',
  'ai.verbosityUnset': 'Not set',
  'ai.serviceTier': 'Service tier',
  'ai.timeout': 'Timeout, seconds',
  'ai.webSearch': 'Web search',
  'ai.webSearchContext': 'Web search context',
  'ai.source': 'Parameters',
  'ai.sourceCustom': 'Customized',
  'ai.sourceDefault': 'Default',
  'ai.tableLabel': 'AI prompts',
  'ai.actions': 'Actions',
  'ai.editNamed': 'Edit {name}',
  'ai.emptyDetail': 'Prompts are registered by the AI service, not from this screen.',
  'ai.noMatches': 'No AI prompts match the filters',
  'ai.noMatchesDetail': 'Clear the filters to see every registered prompt.',
  'ai.editorTitle': 'Execution parameters',
  'ai.editorNote': 'The prompt text and the result schema are fixed by the AI service. These parameters apply to this clinic only.',
  'ai.range': 'Allowed:',
  'ai.outputContract': 'Result schema',
  'ai.outputContractFixed': 'Fixed by the prompt',
  'ai.save': 'Save',
  'ai.saving': 'Saving',
  'ai.saved': 'Saved',
  'ai.saveFailed': 'Could not save the execution parameters',
  'ai.saveForbidden': 'You may not change AI prompt parameters for this clinic',
  'ai.saveConflict': 'Someone else changed these parameters. Close the panel and open it again.',
  'ai.invalidDraft': 'A value is outside the allowed range',
  'ai.unsavedIntent': 'AI prompt settings \u2014 edits that were never saved',
  'ai.reset': 'Restore defaults',
  'ai.resetting': 'Restoring',
  'ai.files.title': 'Prompt support files',
  'ai.files.note':
    'Translation and dictation runs attach these files to the prompt: source-language modules, target-style modules and glossaries. Each file is uploaded to OpenAI once per account and referenced by its id afterwards.',
  'ai.files.loading': 'Loading AI prompt files',
  'ai.files.loadFailed': 'Could not load AI prompt files',
  'ai.files.tableLabel': 'AI prompt files',
  'ai.files.empty': 'No support files are registered',
  'ai.files.emptyDetail': 'Support files arrive with the seeded dataset.',
  'ai.files.file': 'File',
  'ai.files.slot': 'Role',
  'ai.files.slotSource': 'Source language',
  'ai.files.slotTarget': 'Target style',
  'ai.files.slotGlossary': 'Glossary',
  'ai.files.language': 'Language',
  'ai.files.size': 'Size',
  'ai.files.source': 'Content',
  'ai.files.sourceSeed': 'Seed',
  'ai.files.sourceCustom': 'Customized',
  'ai.files.openai': 'OpenAI',
  'ai.files.openaiCurrent': 'Uploaded',
  'ai.files.openaiStale': 'Re-upload pending',
  'ai.files.downloadNamed': 'Download {name}',
  'ai.files.replaceNamed': 'Replace {name}',
  'ai.files.replacePicker': 'Choose a replacement file',
  'ai.files.replaced': 'Saved {file}. The next prompt run uploads it to OpenAI.',
  'ai.files.replaceInvalid': 'The replacement must be a UTF-8 text file up to 1 MiB.',
  'ai.files.replaceConflict': 'Someone else changed this file. The list shows the current content; pick the file again.',
  'ai.files.replaceFailed': 'Could not replace {file}',
} as const
